import { formatDate } from '@angular/common';

// Learner-attention states for the dashboard: the activities whose report is
// waiting on the learner. Everything else keeps the existing status-dot logic.
//
//   ready_for_review  recording feedback or PYI report released, or LMS scored,
//                     and the learner hasn't opened it
//   report_viewed     recording feedback opened but the activity isn't Completed
//                     yet (v2/v2.1 reports complete via "save next steps")
//   upload_needed     recording whose background survey is done but whose
//                     recording hasn't been uploaded yet
//
// Derived purely from fields already on the activity doc plus the client-set
// `locked` flag; a locked activity is never an attention row.
export type LearnerAttentionState = 'ready_for_review' | 'report_viewed' | 'upload_needed' | null;

export function getLearnerAttentionState(activity: any): LearnerAttentionState {
  if (!activity || activity.locked) {
    return null;
  }

  if (activity.type == 'recording') {
    if (!activity.recordingId && activity.status != 'Completed' && activity.status != 'Closed' &&
        (activity.bcsCompletedAt || activity.reviewStatus == 'Awaiting Recording')) {
      return 'upload_needed';
    }
    if (activity.reviewStatus == 'Published' && activity.status != 'Completed') {
      return activity.reportViewedAt ? 'report_viewed' : 'ready_for_review';
    }
    return null;
  }

  // LMS: the score lands on the activity while it's still In Progress; the
  // learner opening the score report (reporting.component) completes it.
  if (activity.type == 'lms' && activity.status == 'In Progress' && activity.score?.value &&
      !activity.withACoach && activity.reviewStatus != 'Coach Review') {
    return 'ready_for_review';
  }

  if (activity.type == 'pyi' && activity.reviewStatus == 'Published') {
    // Self-review PYI: the scorer publishes while the activity is still In Progress
    // and the report page completes it on open.
    if (activity.status == 'In Progress') {
      return 'ready_for_review';
    }
    // Coach-review PYI: the coach's publish sets Completed in the same write, so
    // only reportViewedAt tells us the learner opened it. publishDate gates out
    // PYIs published before that field existed.
    if (activity.withACoach && activity.status == 'Completed' && activity.publishDate && !activity.reportViewedAt) {
      return 'ready_for_review';
    }
  }

  return null;
}

export function needsLearnerAction(activity: any): boolean {
  return getLearnerAttentionState(activity) == 'ready_for_review';
}

// A failed LMS attempt (Completed, score under 80) is 'retaken' once another
// attempt of the same LMS exists that is still open, passed, or finished later;
// otherwise it still needs a retake. Same LMS = same course link (name if none).
// Closed attempts were withdrawn, so they never count as the retake.
// Pass the full unfiltered list so attempts hidden from display still count.
export type LmsRetakeState = 'needed' | 'retaken' | null;

export function getLmsRetakeState(activity: any, activities: any[]): LmsRetakeState {
  if (!isFailedLmsAttempt(activity)) {
    return null;
  }
  const key = lmsAttemptKey(activity);
  const finishedAt = (a: any) => new Date(a.completionDate || 0).getTime();
  const hasNewerAttempt = activities.some((other: any) =>
    other !== activity && other.type == 'lms' && other.status != 'Closed' && lmsAttemptKey(other) == key && (
      other.status != 'Completed' ||
      !isFailedLmsAttempt(other) ||
      finishedAt(other) > finishedAt(activity)
    )
  );
  return hasNewerAttempt ? 'retaken' : 'needed';
}

function isFailedLmsAttempt(activity: any): boolean {
  return activity?.type == 'lms' && activity.status == 'Completed' && activity.score?.score < 80;
}

function lmsAttemptKey(activity: any): string {
  return activity?.data?.link || activity?.name || '';
}

// One labeled status pill per activity row (dashboard and skill view); first
// match wins. Expects applyPrerequisiteLocks + applyAttentionState to have run.
// Keys map to the .pill--<key> classes in src/styles.scss.
export function getActivityStatusPill(activity: any): { key: string, label: string } {
  if (activity.locked) return { key: 'locked', label: 'Locked' };
  if (activity.attentionState == 'upload_needed') return { key: 'upload_needed', label: 'Upload needed' };
  if (activity.attentionState == 'ready_for_review') return { key: 'ready_for_review', label: 'Ready for review' };
  if (activity.attentionState == 'report_viewed') return { key: 'next_steps', label: 'Next steps needed' };
  // A coach can mark an activity completed while it's still in Coach Review
  if (activity.reviewStatus == 'Coach Review' && activity.status != 'Completed') return { key: 'coach_review', label: 'With coach' };
  if (activity.retakeState == 'needed') return { key: 'retake', label: 'Retake needed' };
  if (activity.retakeState == 'retaken') return { key: 'retaken', label: 'Retaken' };
  if (activity.status == 'Completed') return { key: 'completed', label: 'Completed' };
  if (activity.status == 'In Progress') return { key: 'in_progress', label: 'In progress' };
  return { key: 'not_started', label: 'Not started' };
}

// Started / Completed dates shown as a tooltip on the Due On cell.
export function getActivityDatesTooltip(activity: any): string {
  const fmt = (d: any) => {
    if (!d) return '';
    try { return formatDate(d, 'mediumDate', 'en-US'); } catch { return ''; }
  };
  const lines = [];
  const started = fmt(activity?.startDate);
  const completed = fmt(activity?.completionDate);
  if (started) lines.push(`Started: ${started}`);
  if (completed) lines.push(`Completed: ${completed}`);
  return lines.join('\n');
}

// Score meter (dashboard + skill view): LMS pass state and bar width (0-100).
export function isLmsPassed(activity: any): boolean {
  return activity?.score?.score >= 80;
}

export function getLmsScorePct(activity: any): number {
  const raw = activity?.score?.score ?? parseFloat(activity?.score?.value);
  const pct = Number(raw);
  return isNaN(pct) ? 0 : Math.max(0, Math.min(100, pct));
}

// Recording meter tooltip, e.g. "Basic · High"; just the level when there's no granularity.
export function getSkillDemonstrationTooltip(activity: any, dots: number): string {
  const level = activity?.skillDemonstration?.level;
  const lmh = ['', 'Low', 'Medium', 'High'][dots];
  return lmh ? `${level} · ${lmh}` : level;
}
